// Requests whose refusal the page renders itself — in its form, under a field, in its own dialog or in its own alert —
// so the interceptor shows nothing for them (403 included). Loads with a fixed "could not load" line stay under the
// common alerts; a URL shared with a screen that relies on the common alert is not listed, unless the method tells the
// two apart (an entry with `methods`).
type InlineErrorUrl = RegExp | { pattern: RegExp; methods: string[] }

const INLINE_ERROR_URLS: InlineErrorUrl[] = [
  // reason dictionaries: create and edit (reorder is carved out by `-reason/reorder`; lists and lookups are not)
  /\/internal\/(deposit-cancel|dispute|overpayment)-reason(\/\d+)?$/,
  // overpayment creation and its payment through the local adapter (the journal and its export are not listed)
  /\/internal\/overpayment(\/\d+\/pay-via-local-adapter)?$/,
  // payout requisite replacement and the snapshot rebuild after it
  /\/matching-ticket\/payout\/[^/]+\/requisite\/(replace|rebuild-snapshot)$/,
  // merchant create wizard
  /\/internal\/merchant$/,
  // bulk transfer of a requisite's clients
  /\/internal\/requisite\/\d+\/bulk-transfer-clients$/,
  // enabling / disabling a requisite: the card conflict names the conflicting requisite in the page's alert
  /\/internal\/requisite\/toggle-active\/\d+$/,
  // hiding a requisite: the refusal is the confirmation dialog's validation message
  /\/internal\/requisite\/hide\/\d+$/,
  // client risk score corrections (field errors in the modal)
  /\/internal\/external_user\/[^/]+\/risk-score(\/temporary)?$/,
  // payout completion dialog with the commission field
  /\/api\/payout\/complete\/[^/]+$/,
  // commission preview
  /\/commission-rules\/preview$/,
  // deposit actions answered by the page's own alert: complete, decline, change of the expected amount
  /\/api\/(internal\/)?payment\/(complete|decline|change-expected-amount)\/[^/]+$/,
  // payout decline on the payout card
  /\/api\/payout\/decline\/[^/]+$/,
  // receipt verification and rejection
  /\/api\/payment\/bill\/\d+\/(verify|rejected)$/,
  // client and cash box creation, detaching a client's payment requisite, an adapter's minimum deposit
  /\/internal\/external_user\/create$/,
  /\/internal\/cash-box\/create$/,
  /\/internal\/external_user\/[^/]+\/payment-requisite\/\d+$/,
  /\/internal\/payment-adapter\/\d+\/edit$/,
  // adapter priorities and their configuration: saved from the priorities page and the routing editor
  /\/internal\/payment-adapter\/priority\/(config)?$/,
  // requisite type edit and creation (their own force dialog and alert; creation shares the list's URL, so by method)
  /\/internal\/requisite-type\/\d+$/,
  { pattern: /\/internal\/requisite-type$/, methods: ['post'] },
  // the message template steps
  /\/sms\/message-template(\/(match|generate))?$/,
  // callback resend: the refusal is the confirmation dialog's validation message
  /\/internal\/callback\/resend$/,
  // a cash box's programmatic access: switching it and rotating its key answer in the card's own alert
  /\/internal\/cash-box\/\d+\/api-access(\/rotate)?$/,
]

const matches = (entry: InlineErrorUrl, url: string, method?: string): boolean =>
  entry instanceof RegExp
    ? entry.test(url)
    : entry.pattern.test(url) && !!method && entry.methods.includes(method.toLowerCase())

export const rendersErrorInline = (url?: string, method?: string): boolean =>
  !!url && INLINE_ERROR_URLS.some((entry) => matches(entry, url, method))
